/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

import { isEmpty } from 'serenova-js-utils/strings';
import { validateEmail } from 'serenova-js-utils/validation';

const emailValidation = (email) => {
  if (isEmpty(email)) {
    return 'Please enter an email address';
  } else if (!validateEmail(email)) {
    return 'Please enter a valid email address';
  } else {
    return false;
  }
};

export const formValidation = (values, props) => {
  let validation = {};
  const integrationType = values.get('type');
  const properties = values.get('properties');
  validation.name = isEmpty(values.get('name')) && 'Please enter a name';
  validation.type = !values.get('type') && 'Please select a type';

  if (properties) {
    if (integrationType === 'rest' || integrationType === 'zendesk') {
      /**
       * endpointPrefix allows empty string for REST integrations,
       * https://lifesize.atlassian.net/browse/CXV1-23989
       */
      validation.properties = {
        endpointPrefix:
          integrationType !== 'rest' && isEmpty(properties.get('endpointPrefix')) && 'Please enter an API URI',
        username: isEmpty(properties.get('username')) && 'Please enter a Username',
        password: isEmpty(properties.get('password')) && 'Please enter a Password',
        token: isEmpty(properties.get('token')) && 'Please enter a Token',
      };
    }

    if (integrationType === 'monet') {
      const pattern = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
      validation.properties = {
        monetUrl: !pattern.test(properties.get('monetUrl')) && 'Please enter a valid URL',
        username: isEmpty(properties.get('username')) && 'Please enter a Username',
        password: isEmpty(properties.get('password')) && 'Please enter a Password',
      };
    }

    if (integrationType === 'agent-assist') {
      const pattern = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
      validation.properties = {
        agentAssistUrl:
          !isEmpty(properties.get('agentAssistUrl')) &&
          !pattern.test(properties.get('agentAssistUrl')) &&
          'Please enter a valid URL',
      };
    }

    if (integrationType === 'email') {
      validation.properties = {
        incomingType: isEmpty(properties.get('incomingType')) && 'Please enter an Incoming Protocol',
        oauthCredential: isEmpty(properties.get('oauthCredential')) && 'Please generate the Oauth Credential',
        imapServer: isEmpty(properties.get('imapServer')) && 'Please enter an Incoming IMAP Host',
        smtpHost: isEmpty(properties.get('smtpHost')) && 'Please enter a SMTP Host',
        smtpPort: isEmpty(properties.get('smtpPort')) && 'Please enter a SMTP Port',
        smtpEncryptionType: isEmpty(properties.get('smtpEncryptionType')) && 'Please select an SMTP Encryption',
        smtpUser: isEmpty(properties.get('smtpUser')) && 'Please enter a SMTP User',
        smtpPassword: isEmpty(properties.get('smtpPassword')) && 'Please enter a SMTP Password',
      };
    }

    if (integrationType === 'salesforce') {
      // Branch validation by authVersion
      const authVersion = properties.get('authVersion');
      const isNewIntegration = !values.get('id');
      if (authVersion === 'v2-auth-code' || (isNewIntegration && !authVersion)) {
        validation.properties = {
        };
      } else {
        validation.properties = {
          username: isEmpty(properties.get('username')) && 'Please enter a Username',
          password: isEmpty(properties.get('password')) && 'Please enter a Password',
          consumerKey: isEmpty(properties.get('consumerKey')) && 'Please enter a Consumer Key',
          consumerSecret: isEmpty(properties.get('consumerSecret')) && 'Please enter a Consumer Secret',
          securityToken: isEmpty(properties.get('securityToken')) && 'Please select a Security Token',
        };
      }
    }
    if (integrationType === 'ms-teams') {
      validation.properties = {
        adUserEmail: emailValidation(properties.get('adUserEmail')),
        adUserPassword: isEmpty(properties.get('adUserPassword')) && 'Please enter a Password',
        adTenantId: isEmpty(properties.get('adTenantId')) && 'Please enter Azure AD Tenant ID',
        adAppId: isEmpty(properties.get('adAppId')) && 'Please enter Azure AD Application ID',
        adAppSecret: isEmpty(properties.get('adAppSecret')) && 'Please enter Azure AD Application Secret',
        sbcFqdn: isEmpty(properties.get('sbcFqdn')) && 'Please enter SBC FQDN',
      };
    }
    if (integrationType === 'msteams-presence') {
      validation.properties = {
        msTenantId: isEmpty(properties.get('msTenantId')) && 'Please enter MS Tenant ID',
        msClientId: isEmpty(properties.get('msClientId')) && 'Please enter MS Client/Application ID',
      };
    }
    if (integrationType === 'activedir') {
      validation.properties = {
        azureTenantId: isEmpty(properties.get('azureTenantId')) && 'Please enter Azure Tenant Id',
        applicationId: isEmpty(properties.get('applicationId')) && 'Please enter the Application Id',
        secret: isEmpty(properties.get('secret')) && 'Please enter secret',
        // groupsFilter - no validation
        // locationsFilter - no validation
      };
    }
    if (integrationType === 'salesforce-service-cloud') {
      validation.properties = {
        username: isEmpty(properties.get('username')) && 'Please enter a User Name',
        password: isEmpty(properties.get('password')) && 'Please enter a Password',
        securityToken: isEmpty(properties.get('securityToken')) && 'Please enter Security Token',
        consumerKey: isEmpty(properties.get('consumerKey')) && 'Please enter Consumer Key',
        consumerSecret: isEmpty(properties.get('consumerSecret')) && 'Please enter Consumer Secret',
        baseUrl: isEmpty(properties.get('baseUrl')) && 'Please enter Base URL',
        baseUrlTelephony: isEmpty(properties.get('baseUrlTelephony')) && 'Please enter Base URL Telephony',
        organisationId: isEmpty(properties.get('organisationId')) && 'Please enter Organisation ID',
        callCenterName: isEmpty(properties.get('callCenterName')) && 'Please enter CallCenter Name',
        certificate: isEmpty(properties.get('certificate')) && 'Please enter Certificate',
      };
    }

    if (integrationType === 'twilio') {
      const extensionPrefixPattern = /^[\d]{5}?$/;
      const webRtcValidation = () => {
        if (properties.get('webRtc')) {
          if (!values.get('webRtcLabel') || isEmpty(values.get('webRtcLabel'))) {
            return 'Please enter WebRTC label';
          } else if (values.get('webRtcLabel').length > 50) {
            return 'Maximum of 50 characters only allowed as WebRTC label';
          }
        }
      };
      validation.properties = {
        accountSid: isEmpty(properties.get('accountSid')) && 'Please enter a valid URL',
        authToken: isEmpty(properties.get('authToken')) && 'Please enter an AuthToken',
        region: isEmpty(properties.get('region')) && 'Please select a Region',
        extensionPrefix:
          !isEmpty(properties.get('extensionPrefix')) &&
          !properties.get('extensionPrefix').match(extensionPrefixPattern) &&
          'Extension Prefix should only contain a 5 length numeric value',
      };
      validation.webRtcLabel = webRtcValidation();
    }

    if (integrationType === 'ecg') {
      const extensionPrefixPattern = /^[\d]{5}?$/;
      const ecgWebRtcValidation = () => {
        if (properties.get('webRtc')) {
          if (!values.get('ecgWebRtcLabel') || isEmpty(values.get('ecgWebRtcLabel'))) {
            return 'Please enter ECG WebRTC label';
          } else if (values.get('ecgWebRtcLabel').length > 50) {
            return 'Maximum of 50 characters only allowed as WebRTC label';
          }
        }
      };
      validation.properties = {
        trunkId: isEmpty(properties.get('trunkId')) && 'Please enter Trunk ID',
        extensionPrefix:
          !isEmpty(properties.get('extensionPrefix')) &&
          !properties.get('extensionPrefix').match(extensionPrefixPattern) &&
          'Extension Prefix should only contain a 5 length numeric value',
      };
      validation.ecgWebRtcLabel = ecgWebRtcValidation();
    }
  }

  return validation;
};
